import { useEffect, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { Button, Textarea, Typography } from "@platform/design-system";

import { useAutosaveFlush } from "../../hooks/useAutosaveFlush";
import useToastNotificationService from "../../hooks/useToastNotificationService";
import { useRfpNote } from "../../hooks/useRfpNote";

const MAX_LENGTH = 4096;

interface RFPNoteContentProps {
  ecoRfpId: string;
  onClose: VoidFunction;
  registerFlush?: (flush: () => void) => void;
}

const RFPNoteContent = ({
  ecoRfpId,
  onClose,
  registerFlush,
}: RFPNoteContentProps): React.JSX.Element => {
  const noteHook = useRfpNote(ecoRfpId);
  const { createToastNotification } = useToastNotificationService();
  const [noteValue, setNoteValue] = useState(noteHook.content);

  // Prevents autosave from re-writing what a manual save just persisted.
  const lastPersistedRef = useRef(noteHook.content);

  useEffect(() => {
    setNoteValue(noteHook.content);
    lastPersistedRef.current = noteHook.content;
  }, [noteHook.content]);

  const handleNoteChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    setNoteValue(event.target.value);
  };

  const trimmedNote = noteValue.trim();
  const noteSize = trimmedNote.length;
  const isOverLimit = noteSize > MAX_LENGTH;
  const charsOverLimit = noteSize - MAX_LENGTH;
  const isEmpty = noteSize === 0;
  const hasChanged = trimmedNote !== noteHook.content;

  const persist = async (content: string): Promise<void> => {
    await noteHook.upsertNoteAsync({ content });
    lastPersistedRef.current = content;
  };

  const handleSave = async (): Promise<void> => {
    await persist(trimmedNote);
    onClose();
  };

  // Autosave skips empty/over-limit and content already persisted.
  useAutosaveFlush(() => {
    const canAutosave =
      hasChanged &&
      !isEmpty &&
      !isOverLimit &&
      trimmedNote !== lastPersistedRef.current;
    if (!canAutosave) return;
    void persist(trimmedNote).catch(() => {
      createToastNotification({
        severity: "error",
        title: "Saving failed",
        message: "Your RFP notes weren't saved. Please try again.",
      });
    });
  }, registerFlush);

  if (noteHook.isLoading)
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="textSecondary">
          Loading notes...
        </Typography>
      </Box>
    );

  if (noteHook.isError)
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="danger">
          Failed to load notes. Please try again.
        </Typography>
      </Box>
    );

  return (
    <Box
      role="tabpanel"
      id="tabpanel-rfp"
      aria-labelledby="tab-rfp"
      sx={{
        display: "grid",
        gridTemplateRows: "1fr auto",
        gap: 2,
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ minHeight: 0, containerType: "size" }}>
        <Textarea
          value={noteValue}
          placeholder="RFP Notes"
          onChange={handleNoteChange}
          characterLimitMode="warn"
          characterCounterVisible
          error={isOverLimit}
          maxLength={MAX_LENGTH}
          minRows={1}
          helperText={
            isOverLimit
              ? `${charsOverLimit} characters over the limit`
              : undefined
          }
          fullWidth
          sx={{
            "& .MuiInputBase-inputMultiline": {
              resize: "none",
              overflow: "auto !important",
              maxHeight: `calc(100cqh - ${isOverLimit ? 72 : 52}px)`,
            },
          }}
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="tertiary"
          onClick={onClose}
          disabled={noteHook.isUpserting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            !hasChanged || isEmpty || isOverLimit || noteHook.isUpserting
          }
          onClick={handleSave}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default RFPNoteContent;
